import type {m_taxonomy} from "~/services/classes/TaxonomyClasses";

export type IndexedCategory = {
    id: number;
    label: string;
    description: string;
    depth: number;
    parentId: number | null;
    /** Ids from L1 down to this category */
    path: number[];
};

export type TaxonomyIndex = {
    byId: ReadonlyMap<number, IndexedCategory>;
    /** Categories per level; index 0 = L1 */
    levels: IndexedCategory[][];
};

/** Flat lookup of the (non-removed) categories, used by the level filters. */
export function indexTaxonomy(root: m_taxonomy.Tree): TaxonomyIndex {
    const byId = new Map<number, IndexedCategory>();
    const levels: IndexedCategory[][] = [];
    const visit = (n: m_taxonomy.Tree, depth: number, parentId: number | null, path: number[]) => {
        for (const c of n.children) {
            if (c.removed) continue;
            const cat: IndexedCategory = {
                id: c.id,
                label: c.label,
                description: c.values.description ?? '',
                depth,
                parentId,
                path: [...path, c.id],
            };
            byId.set(c.id, cat);
            (levels[depth - 1] ??= []).push(cat);
            visit(c, depth + 1, c.id, cat.path);
        }
    };
    visit(root, 1, null, []);
    return {byId, levels};
}

/** The category chosen per level (index 0 = L1); a level may be left empty. */
export type LevelSelection = readonly (number | undefined)[];

/** Options for a level: the categories under the deepest choice above it, or the whole level. */
export function levelOptions(index: TaxonomyIndex, levelIdx: number, selection: LevelSelection): IndexedCategory[] {
    const anchor = selection.slice(0, levelIdx).findLast(id => id !== undefined);
    const all = index.levels[levelIdx] ?? [];
    return anchor === undefined ? all : all.filter(c => c.path.includes(anchor));
}

/**
 * Choose (or clear) one level. Upper levels never change; lower choices no longer under an upper
 * choice are cleared (an upper choice narrows the levels below it, not the other way around).
 */
export function selectLevel(index: TaxonomyIndex, selection: LevelSelection, levelIdx: number, id: number | undefined): LevelSelection {
    const next = [...selection];
    next[levelIdx] = id;
    for (let j = levelIdx + 1; j < next.length; j++) {
        const lower = next[j] === undefined ? undefined : index.byId.get(next[j]!);
        if (lower && next.slice(0, j).some(upper => upper !== undefined && !lower.path.includes(upper))) next[j] = undefined;
    }
    while (next.length > 0 && next.at(-1) === undefined) next.pop();
    return next;
}

/** The deepest chosen category that still exists in the tree. */
export function deepestSelected(index: TaxonomyIndex, selection: LevelSelection): IndexedCategory | undefined {
    const id = selection.findLast(id => id !== undefined && index.byId.has(id));
    return id === undefined ? undefined : index.byId.get(id);
}

/** Categories that have sub-categories, with their level (1 = L1). */
function parentsWithDepth(index: TaxonomyIndex): IndexedCategory[] {
    const parentIds = new Set<number>();
    index.byId.forEach(c => { if (c.parentId !== null) parentIds.add(c.parentId); });
    return [...parentIds].flatMap(id => index.byId.get(id) ?? []);
}

/** The open categories that show the tree down to `level` (1 = only L1): every branch above it is open. */
export function expandedToLevel(index: TaxonomyIndex, rootId: number, level: number): Set<number> {
    return new Set([rootId, ...parentsWithDepth(index).filter(c => c.depth < level).map(c => c.id)]);
}

/** The level the open categories show exactly (see `expandedToLevel`), or undefined for a mixed state. */
export function levelOfExpanded(index: TaxonomyIndex, rootId: number, expanded: ReadonlySet<number>): number | undefined {
    for (let level = 1; level <= index.levels.length; level++) {
        const target = expandedToLevel(index, rootId, level);
        if (target.size === expanded.size && [...target].every(id => expanded.has(id))) return level;
    }
    return undefined;
}
